// Curator dashboard — review queue, library, rejected, analytics.
// v2 design DNA: light hero, tabs, cards, soft chips. Mirrors the
// production /curator page actions: approve, reject (with notes),
// feature/unfeature, certify (admin only), delete, revert.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { curatorApi } from "@/lib/api/curator";
import type { CuratorPack } from "@/lib/api/curator";
import { useUiStore } from "@/lib/store/uiStore";

type TabId = "queue" | "library" | "rejected" | "analytics";

const TABS: { id: TabId; label: string }[] = [
  { id: "queue", label: "Review queue" },
  { id: "library", label: "Library" },
  { id: "rejected", label: "Drafts & rejected" },
  { id: "analytics", label: "Analytics" },
];

const STATUS: Record<string, { bg: string; fg: string; label: string }> = {
  built_in: { bg: "var(--eb-50)", fg: "white", label: "Built-in" },
  featured: { bg: "#fde8c2", fg: "#8a5200", label: "Featured" },
  approved: { bg: "#d9efe1", fg: "var(--teal)", label: "Community" },
  draft: { bg: "var(--cb-95)", fg: "var(--ink-2)", label: "Draft" },
  submitted: { bg: "#ede0fb", fg: "var(--violet)", label: "Pending" },
  rejected: { bg: "#fdd9dc", fg: "var(--pink)", label: "Needs changes" },
};

export function Curator(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabId>("queue");

  useEffect(() => {
    document.title = "Curator · Generator";
  }, []);

  if (auth.loading) {
    return <div style={{ padding: 48, textAlign: "center", color: "var(--ink-3)" }}>Loading…</div>;
  }
  if (!auth.isCurator && !auth.isAdmin) {
    return (
      <div style={{ padding: 48, maxWidth: 540, margin: "60px auto", textAlign: "center" }}>
        <div className="card" style={{ padding: 32 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>Curator access required</h2>
          <p style={{ color: "var(--ink-2)", marginTop: 10, fontSize: 13 }}>
            Sign in as a Q Branch curator to manage the marketplace.
          </p>
          <button className="btn primary" style={{ marginTop: 14 }} onClick={() => navigate("/team-login")}>
            Q Branch sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <CuratorHero />
      <section style={{ background: "white", padding: "0 32px", borderBottom: "1px solid var(--bd)" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", display: "flex", gap: 4 }}>
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                background: "transparent",
                border: 0,
                padding: "14px 16px",
                fontSize: 13,
                fontWeight: tab === t.id ? 600 : 500,
                color: tab === t.id ? "var(--eb-50)" : "var(--ink-2)",
                borderBottom: tab === t.id ? "2px solid var(--eb-50)" : "2px solid transparent",
                cursor: "pointer",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>

      <section style={{ padding: "28px 32px", background: "var(--cb-98)", minHeight: "60vh" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          {tab === "queue" && <QueueTab isAdmin={auth.isAdmin} />}
          {tab === "library" && <LibraryTab isAdmin={auth.isAdmin} />}
          {tab === "rejected" && <RejectedTab />}
          {tab === "analytics" && <AnalyticsTab />}
        </div>
      </section>
    </div>
  );
}

function CuratorHero(): JSX.Element {
  const { data: analytics } = useQuery({ queryKey: ["curator", "analytics"], queryFn: curatorApi.analytics });
  return (
    <section className="hero-gradient" style={{ padding: "32px 32px 24px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="label" style={{ marginBottom: 6 }}>Curator console</div>
          <h1 style={{ margin: 0, fontSize: 28, color: "var(--eb-15)", fontWeight: 700 }}>
            Marketplace review
          </h1>
          <p style={{ color: "var(--ink-2)", marginTop: 6, fontSize: 13, maxWidth: 640 }}>
            Approve community packs, feature standouts, and curate the Q Branch library.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <Pill label="Pending" value={analytics?.pending_review ?? 0} accent="var(--violet)" />
          <Pill label="Total packs" value={analytics?.total ?? 0} />
          <Pill label="Featured" value={analytics?.by_status?.featured ?? 0} accent="#8a5200" />
        </div>
      </div>
    </section>
  );
}

function Pill({ label, value, accent = "var(--eb-50)" }: { label: string; value: number; accent?: string }): JSX.Element {
  return (
    <div className="card" style={{ padding: "10px 16px" }}>
      <div className="label">{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: accent }}>{value}</div>
    </div>
  );
}

// ───────────────── Queue ─────────────────

function QueueTab({ isAdmin }: { isAdmin: boolean }): JSX.Element {
  const { data: queue = [], isLoading } = useQuery({ queryKey: ["curator", "queue"], queryFn: curatorApi.queue });
  return (
    <PackList
      packs={queue ?? []}
      loading={isLoading}
      emptyText="Inbox zero — nothing pending review."
      isAdmin={isAdmin}
      mode="queue"
    />
  );
}

function LibraryTab({ isAdmin }: { isAdmin: boolean }): JSX.Element {
  const { data: all = [], isLoading } = useQuery({
    queryKey: ["curator", "all", "library"],
    queryFn: () => curatorApi.all(),
  });
  const library = (all ?? []).filter((p) => p.status === "approved" || p.status === "featured" || p.status === "built_in");
  return (
    <PackList
      packs={library}
      loading={isLoading}
      emptyText="Library is empty."
      isAdmin={isAdmin}
      mode="library"
    />
  );
}

function RejectedTab(): JSX.Element {
  const { data: all = [], isLoading } = useQuery({
    queryKey: ["curator", "all", "rejected"],
    queryFn: () => curatorApi.all(),
  });
  const list = (all ?? []).filter((p) => p.status === "draft" || p.status === "rejected");
  return (
    <PackList packs={list} loading={isLoading} emptyText="No drafts or rejected packs." isAdmin={false} mode="rejected" />
  );
}

function AnalyticsTab(): JSX.Element {
  const { data: a } = useQuery({ queryKey: ["curator", "analytics"], queryFn: curatorApi.analytics });
  if (!a) return <div style={{ color: "var(--ink-3)", padding: 24, textAlign: "center" }}>Loading…</div>;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      <div className="card" style={{ padding: 18 }}>
        <div className="label" style={{ marginBottom: 10 }}>Top by usage</div>
        {(a.top_by_usage ?? []).length === 0 && <Empty>No usage data.</Empty>}
        {(a.top_by_usage ?? []).map((row) => (
          <Row key={row.id} title={row.name} value={`${row.use_count} uses`} />
        ))}
      </div>
      <div className="card" style={{ padding: 18 }}>
        <div className="label" style={{ marginBottom: 10 }}>Top by rating</div>
        {(a.top_by_rating ?? []).length === 0 && <Empty>No ratings yet.</Empty>}
        {(a.top_by_rating ?? []).map((row) => (
          <Row key={row.id} title={row.name} value={`${row.rating_avg.toFixed(1)} ★`} />
        ))}
      </div>
      <div className="card" style={{ padding: 18, gridColumn: "1 / span 2" }}>
        <div className="label" style={{ marginBottom: 10 }}>Packs by status</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8 }}>
          {Object.entries(a.by_status ?? {}).map(([status, count]) => (
            <div key={status} style={{ padding: 12, background: "var(--cb-98)", borderRadius: 8 }}>
              <div className="label">{status}</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{count}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Row({ title, value }: { title: string; value: string }): JSX.Element {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--bd-2)" }}>
      <div style={{ fontSize: 12, fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: 12, color: "var(--ink-2)" }}>{value}</div>
    </div>
  );
}

// ───────────────── Pack list + actions ─────────────────

function PackList({
  packs,
  loading,
  emptyText,
  isAdmin,
  mode,
}: {
  packs: CuratorPack[];
  loading: boolean;
  emptyText: string;
  isAdmin: boolean;
  mode: "queue" | "library" | "rejected";
}): JSX.Element {
  if (loading) return <div style={{ color: "var(--ink-3)", padding: 24, textAlign: "center" }}>Loading…</div>;
  if (packs.length === 0) {
    return (
      <div className="card" style={{ padding: 32, textAlign: "center", color: "var(--ink-3)", fontSize: 13 }}>
        {emptyText}
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gap: 10 }}>
      {packs.map((p) => (
        <PackCard key={p.id} pack={p} isAdmin={isAdmin} mode={mode} />
      ))}
    </div>
  );
}

function PackCard({ pack, isAdmin, mode }: { pack: CuratorPack; isAdmin: boolean; mode: "queue" | "library" | "rejected" }): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const showToast = useUiStore((s) => s.showToast);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNotes, setRejectNotes] = useState("");

  const refetch = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["curator"] });
  };

  const approve = useMutation({
    mutationFn: () => curatorApi.approve(pack.id),
    onSuccess: () => { showToast("Approved"); refetch(); },
    onError: (e) => showToast(`Approve failed: ${(e as Error).message}`),
  });
  const reject = useMutation({
    mutationFn: () => curatorApi.reject(pack.id, rejectNotes),
    onSuccess: () => { setRejectOpen(false); setRejectNotes(""); showToast("Rejected with notes"); refetch(); },
    onError: (e) => showToast(`Reject failed: ${(e as Error).message}`),
  });
  const feature = useMutation({
    mutationFn: () => curatorApi.feature(pack.id),
    onSuccess: () => { showToast("Featured ✦"); refetch(); },
  });
  const unfeature = useMutation({
    mutationFn: () => curatorApi.unfeature(pack.id),
    onSuccess: () => { showToast("Unfeatured"); refetch(); },
  });
  const certify = useMutation({
    mutationFn: () => curatorApi.certify(pack.id),
    onSuccess: () => { showToast("Certified · built-in"); refetch(); },
  });
  const del = useMutation({
    mutationFn: () => curatorApi.delete(pack.id),
    onSuccess: () => { showToast("Deleted"); refetch(); },
  });
  const revert = useMutation({
    mutationFn: () => curatorApi.revert(pack.id),
    onSuccess: () => { showToast("Reverted to previous version"); refetch(); },
  });

  const status = STATUS[pack.status] ?? STATUS.draft;
  const isBuiltInFile = pack.source === "built_in" || pack.status === "built_in";

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 16, alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
          <PackLogo pack={pack} />
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{pack.name}</div>
              <span
                style={{
                  background: status.bg,
                  color: status.fg,
                  fontSize: 10,
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: 999,
                }}
              >
                {status.label}
              </span>
              {pack.industry && (
                <span className="chip ghost" style={{ fontSize: 10 }}>{pack.industry}</span>
              )}
            </div>
            <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4 }}>
              {pack.creator_email ? `by ${pack.creator_email} · ` : ""}
              {pack.use_count != null ? `${pack.use_count} uses · ` : ""}
              {pack.rating_avg ? `${pack.rating_avg.toFixed(1)} ★ ` : ""}
              {pack.version != null ? `v${pack.version}` : ""}
            </div>
            {pack.description && (
              <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 4, maxWidth: 720 }}>
                {pack.description}
              </div>
            )}
            {pack.notes && (
              <div style={{ fontSize: 11, color: "var(--ink-2)", marginTop: 6, padding: 8, background: "var(--cb-98)", borderRadius: 6 }}>
                <strong>Notes:</strong> {pack.notes}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {mode === "queue" && (
            <>
              <button className="btn primary sm" onClick={() => approve.mutate()} disabled={approve.isPending}>
                Approve
              </button>
              <button className="btn ghost sm" onClick={() => setRejectOpen((o) => !o)}>
                Reject
              </button>
            </>
          )}
          {mode === "library" && (
            <>
              {pack.status === "featured" ? (
                <button className="btn ghost sm" onClick={() => unfeature.mutate()} title="Remove the Featured badge">Unfeature</button>
              ) : pack.status === "approved" ? (
                <button className="btn ghost sm" onClick={() => feature.mutate()} title="Promote to Featured (highlighted in marketplace)">Promote · Feature</button>
              ) : null}
              {isAdmin && pack.status !== "built_in" && (
                <button
                  className="btn ghost sm"
                  title="Certify and ship as a built-in pack for everyone"
                  onClick={() => {
                    if (confirm(`Make ${pack.name} a Built-in pack? It will ship with the app for all users.`)) {
                      certify.mutate();
                    }
                  }}
                >
                  Make built-in
                </button>
              )}
              <button className="btn ghost sm" onClick={() => revert.mutate()} title="Restore the previous version of this pack">
                Revert version
              </button>
            </>
          )}
          <button
            className="btn ghost sm"
            onClick={() => navigate(`/profiles/${encodeURIComponent(pack.id)}/edit`)}
            title="Open the full pack page (company, products, stories, personas)"
          >
            Open
          </button>
          {!isBuiltInFile && (
            <button
              className="btn danger sm"
              onClick={() => {
                if (confirm(`Permanently delete ${pack.name}?`)) del.mutate();
              }}
            >
              Delete
            </button>
          )}
          <a
            className="btn ghost sm"
            href={`/api/packs/${pack.id}`}
            target="_blank"
            rel="noopener noreferrer"
            title="View the raw JSON for this pack"
          >
            JSON ↗
          </a>
        </div>
      </div>
      {rejectOpen && (
        <div style={{ marginTop: 12, padding: 12, background: "#fdf6f7", borderRadius: 8, border: "1px solid #f3d3d6" }}>
          <div className="label" style={{ marginBottom: 6, color: "var(--pink)" }}>Reject with feedback</div>
          <textarea
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            rows={3}
            placeholder="What needs to change before this can be approved?"
            style={{
              width: "100%",
              border: "1px solid var(--bd)",
              borderRadius: 6,
              padding: 10,
              fontSize: 12,
              fontFamily: "inherit",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 8 }}>
            <button className="btn ghost sm" onClick={() => setRejectOpen(false)}>Cancel</button>
            <button
              className="btn danger sm"
              onClick={() => reject.mutate()}
              disabled={!rejectNotes.trim() || reject.isPending}
            >
              Send rejection
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PackLogo({ pack }: { pack: CuratorPack }): JSX.Element {
  const src = pack.logo_url || `/api/packs/${pack.id}/logo`;
  return (
    <div style={{
      width: 48,
      height: 48,
      borderRadius: 10,
      background: "var(--cb-95)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      overflow: "hidden",
    }}>
      <img
        src={src}
        alt=""
        width={48}
        height={48}
        style={{ objectFit: "cover", width: "100%", height: "100%" }}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.display = "none";
        }}
      />
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ textAlign: "center", color: "var(--ink-3)", fontSize: 12, padding: "20px 0" }}>
      {children}
    </div>
  );
}
